import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatErr } from "../../api";
import { downloadFile } from "../../download";
import { SectionTitle, StatusPill, EmptyState, Avatar, RichHtml, stripHtml } from "../../components/Atoms";
import {
  ArrowLeft, Calendar, ListChecks, Users, Megaphone, ChartLineUp,
  FileXls, FileCsv, Funnel, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ClientProjectDetailPage() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);   // approved ambassadors on the project
  const [submissions, setSubmissions] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [filters, setFilters] = useState({ status: "All", creator: "all", task: "all" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, a, s] = await Promise.all([
      api.get(`/projects/${pid}`),
      api.get(`/projects/${pid}/ambassadors`),
      api.get(`/submissions?project_id=${pid}`),
    ]);
    setProject(p.data); setTasks(p.data.tasks || []); setAmbassadors(a.data); setSubmissions(s.data);
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const ambById = useMemo(() => Object.fromEntries(ambassadors.map(a => [a.id, a])), [ambassadors]);

  // Returns creators-on-task rows: [{ amb, subs[] }] — includes ALL approved creators (with a "Not started" row if no submission)
  const creatorRowsForTask = useCallback((tid) => {
    const tsubs = submissions.filter(s => s.task_id === tid);
    const byAmb = {};
    tsubs.forEach(s => { (byAmb[s.ambassador_id] = byAmb[s.ambassador_id] || []).push(s); });
    let rows = ambassadors.map(amb => ({ amb, subs: byAmb[amb.id] || [] }));
    if (filters.creator !== "all") rows = rows.filter(r => r.amb.id === filters.creator);
    if (filters.status !== "All") {
      rows = rows.filter(r => {
        if (filters.status === "Not started") return r.subs.length === 0;
        return r.subs.some(s => s.status === filters.status);
      });
    }
    return rows;
  }, [submissions, ambassadors, filters]);

  const filteredTasks = useMemo(() => {
    if (filters.task === "all") return tasks;
    return tasks.filter(t => t.id === filters.task);
  }, [tasks, filters.task]);

  const act = async (sid, action) => {
    try {
      await api.post(`/submissions/${sid}/${action}`, { feedback: feedback[sid] || "" });
      toast.success(`Submission ${action}d`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const exportXlsx = async () => {
    setBusy(true);
    try { await downloadFile(`/reports/project/${pid}/xlsx`, `project-${pid}.xlsx`); toast.success("Downloaded"); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  const exportCsv = async () => {
    setBusy(true);
    try { await downloadFile(`/reports/project/${pid}/csv`, `project-${pid}.csv`); toast.success("Downloaded"); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!project) return <div className="skeleton h-64" />;
  const taskCount = tasks.length || 1;
  const subCount = submissions.length;
  const ambCount = ambassadors.length;
  const progress = taskCount * Math.max(1, ambCount);
  const pct = progress > 0 ? Math.min(100, Math.round((subCount / progress) * 100)) : 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={() => nav("/client/projects")} className="btn-ghost flex items-center gap-1.5 text-sm" data-testid="back-projects"><ArrowLeft size={14} /> Back to projects</button>
        <div className="flex gap-2 items-center">
          <button onClick={exportXlsx} disabled={busy} className="btn-ghost text-sm flex items-center gap-1.5" data-testid="proj-export-xlsx"><FileXls size={14} weight="bold" /> .xlsx</button>
          <button onClick={exportCsv} disabled={busy} className="btn-ghost text-sm flex items-center gap-1.5" data-testid="proj-export-csv"><FileCsv size={14} weight="bold" /> .csv</button>
        </div>
      </div>

      {/* Header */}
      <div className="glass p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted">{project.category || "—"}</div>
            <div className="text-white text-2xl font-bold mt-1">{project.title}</div>
            {project.description && <RichHtml html={project.description} className="text-sm text-muted mt-2 max-w-2xl" />}
            <div className="text-[11px] text-muted mt-3 flex items-center gap-1"><Calendar size={12} /> {fmtDate(project.start_date)} → {fmtDate(project.end_date)}</div>
          </div>
          <StatusPill status={project.status} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <Stat icon={ListChecks} label="Tasks"        value={taskCount} color="#22D3EE" />
          <Stat icon={Users}      label="Creators"     value={ambCount} color="#EC4899" />
          <Stat icon={Megaphone}  label="Submissions"  value={subCount} color="#F59E0B" />
          <Stat icon={ChartLineUp} label="Progress"    value={`${pct}%`} color="#A3E635" />
        </div>
      </div>

      {/* Filters */}
      <div className="glass p-3 flex flex-wrap gap-2 items-center" data-testid="project-filters">
        <div className="flex items-center gap-1.5 text-xs uppercase font-bold text-muted tracking-wider"><Funnel size={14} weight="bold" /> Filter</div>
        <select value={filters.task} onChange={e => setFilters({...filters, task:e.target.value})} className="input-base text-sm py-1.5 max-w-[240px]" data-testid="proj-filter-task">
          <option value="all">All tasks</option>
          {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
        <select value={filters.creator} onChange={e => setFilters({...filters, creator:e.target.value})} className="input-base text-sm py-1.5 max-w-[240px]" data-testid="proj-filter-creator">
          <option value="all">All creators</option>
          {ambassadors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters({...filters, status:e.target.value})} className="input-base text-sm py-1.5 max-w-[180px]" data-testid="proj-filter-status">
          {["All","Pending","Approved","Rejected","Not started"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Tasks table */}
      <div className="glass p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="tasks-table">
          <thead className="bg-white/5 text-[10px] uppercase font-bold text-muted tracking-wider">
            <tr>
              <th className="text-left p-2.5 w-8"></th>
              <th className="text-left p-2.5">Task</th>
              <th className="text-left p-2.5 whitespace-nowrap">Status</th>
              <th className="text-left p-2.5 hidden md:table-cell">Platforms</th>
              <th className="text-left p-2.5 hidden md:table-cell whitespace-nowrap">Deadline</th>
              <th className="text-right p-2.5 whitespace-nowrap">Reward</th>
              <th className="text-right p-2.5 whitespace-nowrap">Creators</th>
              <th className="text-right p-2.5 whitespace-nowrap">Task Detail</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.length === 0 ? (
              <tr><td colSpan={8} className="p-6 text-center text-muted">No tasks match the filters</td></tr>
            ) : filteredTasks.map(t => {
              const rows = creatorRowsForTask(t.id);
              const platforms = (t.platforms && t.platforms.length > 0)
                ? t.platforms
                : (t.platform ? [t.platform] : []);
              return (
                <tr key={t.id} className="border-t border-white/5 align-middle" data-testid={`task-row-${t.id}`}>
                  <td className="p-2.5" />
                  <td className="p-2.5 text-white font-semibold">{t.title}</td>
                  <td className="p-2.5 whitespace-nowrap"><StatusPill status={t.status || "Active"} /></td>
                  <td className="p-2.5 hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {platforms.map(pl => (
                        <span key={pl} className="text-[10px] uppercase tracking-wider font-bold text-[#22D3EE] bg-[#22D3EE]/10 border border-[#22D3EE]/30 px-2 py-0.5 rounded-full whitespace-nowrap">{pl}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-2.5 text-muted text-xs hidden md:table-cell whitespace-nowrap">{fmtDate(t.deadline)}</td>
                  <td className="p-2.5 text-right text-[#A3E635] font-bold whitespace-nowrap">{fmtMoney((t.reward_amount||0)+(t.bonus_amount||0))}</td>
                  <td className="p-2.5 text-right">
                    <span className="text-white font-bold" data-testid={`task-creator-count-${t.id}`}>{rows.length}</span>
                  </td>
                  <td className="p-2.5 text-right">
                    <Link
                      to={`/client/projects/${pid}/tasks/${t.id}`}
                      data-testid={`task-detail-btn-${t.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#22D3EE]/15 to-[#8B5CF6]/15 border border-[#22D3EE]/30 text-[#22D3EE] hover:from-[#22D3EE]/25 hover:to-[#8B5CF6]/25 hover:text-white text-xs font-bold transition whitespace-nowrap"
                    >
                      <ArrowSquareOut size={12} weight="bold" /> Task Detail
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
    <div className="text-[10px] uppercase tracking-wider text-muted font-bold flex items-center gap-1"><Icon size={12} weight="bold" style={{ color }} /> {label}</div>
    <div className="text-2xl font-black mt-1" style={{ color }}>{value}</div>
  </div>
);